import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Megaphone, Plus, Pencil, Trash2, Loader2, CalendarDays, MapPin, Clock,
  AlertCircle, Info, Bell, Globe, Church, Users, ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/errorHandler';

const TIPO_CONFIG = {
  avviso: { label: 'Avviso', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400', icon: AlertCircle },
  aggiornamento: { label: 'Aggiornamento', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400', icon: Info },
  evento: { label: 'Evento', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', icon: CalendarDays },
};

const PRIORITA_CONFIG = {
  bassa: { label: 'Bassa', color: 'bg-slate-100 text-slate-600' },
  normale: { label: 'Normale', color: 'bg-blue-100 text-blue-700' },
  alta: { label: 'Alta', color: 'bg-red-100 text-red-700' },
};

const VISIBILITA_CONFIG = {
  tutti: { label: 'Tutti', icon: Globe },
  solo_admin: { label: 'Solo Admin', icon: ShieldCheck },
  solo_famiglie: { label: 'Solo Famiglie', icon: Users },
};

const emptyForm = {
  titolo: '', contenuto: '', tipo: 'avviso', priorita: 'normale',
  chiesa_id: '', visibilita: 'tutti', data_evento: '', ora_evento: '', luogo_evento: ''
};

const NewsPage = () => {
  const { API, isSuperAdmin, isAdminChiesa, user } = useAuth();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [chiese, setChiese] = useState([]);
  const canManage = isSuperAdmin() || isAdminChiesa();

  useEffect(() => { fetchNews(); }, []);

  const fetchNews = async () => {
    try {
      const [newsRes] = await Promise.all([axios.get(`${API}/news`)]);
      setNews(newsRes.data);
      if (isSuperAdmin()) {
        const chieseRes = await axios.get(`${API}/chiese`);
        setChiese(chieseRes.data);
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Errore nel caricamento'));
    } finally { setLoading(false); }
  };

  const openNew = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (item) => {
    setEditing(item);
    setForm({
      titolo: item.titolo, contenuto: item.contenuto, tipo: item.tipo,
      priorita: item.priorita, chiesa_id: item.chiesa_id || '',
      visibilita: item.visibilita, data_evento: item.data_evento || '',
      ora_evento: item.ora_evento || '', luogo_evento: item.luogo_evento || ''
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.titolo.trim() || !form.contenuto.trim()) {
      toast.error('Titolo e contenuto sono obbligatori'); return;
    }
    setSaving(true);
    try {
      const payload = { ...form, chiesa_id: form.chiesa_id || null };
      if (editing) {
        await axios.put(`${API}/news/${editing.id}`, payload);
        toast.success('Comunicazione aggiornata');
      } else {
        await axios.post(`${API}/news`, payload);
        toast.success('Comunicazione pubblicata');
      }
      setDialogOpen(false);
      fetchNews();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Errore nel salvataggio'));
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa comunicazione?')) return;
    try {
      await axios.delete(`${API}/news/${id}`);
      toast.success('Comunicazione eliminata');
      fetchNews();
    } catch (err) { toast.error(getErrorMessage(err, 'Errore eliminazione')); }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in" data-testid="news-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold">Comunicazioni</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Avvisi, aggiornamenti e eventi per gli utenti</p>
        </div>
        {canManage && (
          <Button onClick={openNew} data-testid="new-news-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuova
          </Button>
        )}
      </div>

      {news.length === 0 ? (
        <Card>
          <CardContent className="p-8 sm:p-12 text-center">
            <Megaphone className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
            <p className="text-sm text-muted-foreground">Nessuna comunicazione pubblicata</p>
            {canManage && <Button variant="outline" className="mt-4" onClick={openNew}>Pubblica la prima</Button>}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {news.map(item => {
            const tipoConf = TIPO_CONFIG[item.tipo] || TIPO_CONFIG.avviso;
            const TipoIcon = tipoConf.icon;
            const prioConf = PRIORITA_CONFIG[item.priorita] || PRIORITA_CONFIG.normale;
            const isAuthor = item.autore_id === user?.id;
            const canEdit = isSuperAdmin() || isAuthor;

            return (
              <Card key={item.id} data-testid={`news-item-${item.id}`}
                className={item.priorita === 'alta' ? 'border-red-300 dark:border-red-700' : ''}>
                <CardContent className="p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${tipoConf.color}`}>
                      <TipoIcon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="text-sm sm:text-base font-semibold">{item.titolo}</h3>
                        <Badge variant="outline" className={`text-[10px] ${prioConf.color}`}>{prioConf.label}</Badge>
                        <Badge variant="outline" className="text-[10px]">
                          {item.chiesa_id ? (
                            <><Church className="w-3 h-3 mr-1" />{item.chiesa_nome}</>
                          ) : (
                            <><Globe className="w-3 h-3 mr-1" />Globale</>
                          )}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground whitespace-pre-line">{item.contenuto}</p>
                      {item.tipo === 'evento' && (item.data_evento || item.luogo_evento) && (
                        <div className="flex flex-wrap gap-3 mt-2 text-xs text-muted-foreground">
                          {item.data_evento && (
                            <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />{item.data_evento}</span>
                          )}
                          {item.ora_evento && (
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{item.ora_evento}</span>
                          )}
                          {item.luogo_evento && (
                            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{item.luogo_evento}</span>
                          )}
                        </div>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-[11px] text-muted-foreground">
                        <span>di {item.autore_nome}</span>
                        <span>{formatDate(item.created_at)}</span>
                      </div>
                    </div>
                    {canEdit && (
                      <div className="flex gap-1 flex-shrink-0">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(item)} data-testid={`edit-news-${item.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700" onClick={() => handleDelete(item.id)} data-testid={`delete-news-${item.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Modifica Comunicazione' : 'Nuova Comunicazione'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Titolo *</Label>
              <Input value={form.titolo} onChange={e => setForm({...form, titolo: e.target.value})} placeholder="Titolo comunicazione" data-testid="news-titolo-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Contenuto *</Label>
              <Textarea value={form.contenuto} onChange={e => setForm({...form, contenuto: e.target.value})}
                placeholder="Scrivi il messaggio..." rows={4} data-testid="news-contenuto-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Tipo</Label>
                <Select value={form.tipo} onValueChange={v => setForm({...form, tipo: v})}>
                  <SelectTrigger data-testid="news-tipo-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="avviso">Avviso</SelectItem>
                    <SelectItem value="aggiornamento">Aggiornamento</SelectItem>
                    <SelectItem value="evento">Evento</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Priorita</Label>
                <Select value={form.priorita} onValueChange={v => setForm({...form, priorita: v})}>
                  <SelectTrigger data-testid="news-priorita-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bassa">Bassa</SelectItem>
                    <SelectItem value="normale">Normale</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Visibilita</Label>
                <Select value={form.visibilita} onValueChange={v => setForm({...form, visibilita: v})}>
                  <SelectTrigger data-testid="news-visibilita-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tutti">Tutti</SelectItem>
                    <SelectItem value="solo_admin">Solo Admin</SelectItem>
                    <SelectItem value="solo_famiglie">Solo Famiglie</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {isSuperAdmin() && (
                <div className="space-y-1.5">
                  <Label>Destinatari</Label>
                  <Select value={form.chiesa_id || '__global__'} onValueChange={v => setForm({...form, chiesa_id: v === '__global__' ? '' : v})}>
                    <SelectTrigger data-testid="news-chiesa-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__global__">Tutte le chiese</SelectItem>
                      {chiese.map(c => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            {form.tipo === 'evento' && (
              <div className="space-y-3 p-3 rounded-lg border bg-muted/30">
                <p className="text-xs font-medium text-muted-foreground">Dettagli Evento</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Data</Label>
                    <Input type="date" value={form.data_evento} onChange={e => setForm({...form, data_evento: e.target.value})} data-testid="news-data-evento" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Ora</Label>
                    <Input type="time" value={form.ora_evento} onChange={e => setForm({...form, ora_evento: e.target.value})} data-testid="news-ora-evento" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Luogo</Label>
                  <Input value={form.luogo_evento} onChange={e => setForm({...form, luogo_evento: e.target.value})} placeholder="es: Chiesa di Cristo, Via Roma 15" data-testid="news-luogo-evento" />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="news-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editing ? 'Aggiorna' : 'Pubblica'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NewsPage;
